// START IMPORTS
// Hooks imports
import { useCustomPlanUpgrade } from '@ltvco/refresh-lib/hooks';
// Payments imports
import {
  PaymentsSelectionModal,
  RadioPaymentsModal,
  UpgradeFailureModal,
  UpgradePlan,
  UpgradeSuccessModal,
  useGetPaymentType as getPaymentType,
  type Plan,
  useGetPlans,
  useGetPaymentMethod,
  useProcessCard,
  useProcessPayPal,
  useProcessBraintree,
  useSubmitLegal,
  filterActivePaymentMethod,
  ProcessingSpinnerModal,
  TosModal,
} from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import { Grid, styled, CircularProgress } from '@ltvco/refresh-lib/theme';
// Core lib v1 imports
import {
  enqueueSnackbar,
  PaymentMethods,
  useAccount,
} from '@ltvco/refresh-lib/v1';
// Vendor imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// App-specific imports
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';
import { useQueryParams } from 'utils/useQueryParams';

import {
  CurrentPlan,
  getBillingFrequency,
  getCustomPlan,
  getPlanTitleAndDescription,
  getPlans,
  planCostPerReport,
} from './plans';
// END IMPORTS

// START STYLED_COMPONENTS
const UpgradePlanContent = styled(Grid)(({ theme }) => ({
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3.5, 2),
  marginTop: 'max(0px,calc(40px - 1vw))',
  paddingLeft: 0,
  paddingRight: 0,
}));
// END STYLED_COMPONENTS

// START INTERFACES_AND_TYPES
enum State {
  None,
  SelectPayment,
  AddPayment,
  ProcessingPayment,
  Success,
  Error,
}

interface UpgradePlanPageProps {
  contactUsHandler: () => void;
}
// END INTERFACES_AND_TYPES

export const UpgradePlanPage = ({ contactUsHandler }: UpgradePlanPageProps) => {
  // START HOOKS_AND_QUERIES
  const isInDownsellVariation = useFeatureIsOn('prop-2185');
  const { refetch: refetchAccount } = useAccount(true);
  const {
    accountLoaded,
    isStaff,
    isLimitedUser,
    isCancelledSubscription,
    planAmount: amount,
    reportLimit: limit,
    renewalPeriod,
  } = useLimitedPlanInfo();
  const navigate = useNavigate();
  const customPlanId = useQueryParams().get('plan_id');
  const brandPlans = useGetPlans();
  const customPlanUpgrade = customPlanId
    ? useCustomPlanUpgrade(customPlanId)
    : null;
  const updateLegalQuery = useSubmitLegal();
  const paymentMethodQuery = useGetPaymentMethod();
  const processCardQuery = useProcessCard();
  const processPayPalQuery = useProcessPayPal();
  const processBraintreeQuery = useProcessBraintree();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [upgradeState, setUpgradeState] = useState(State.None);
  const [openTosModal, setOpenTosModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const [billingFrecuency, setBillingFrecuency] = useState('monthly');
  const [billedPrice, setBilledPrice] = useState('');
  const [upgradeErrors, setUpgradeErrors] = useState<string[]>([]);
  const [hadPaymentErrors, setHadPaymentErrors] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const isState = (state: State) => upgradeState === state;
  const title = isStaff ? 'Staff' : getPlanTitleAndDescription(limit).title;
  const currentPlan: CurrentPlan = useMemo(() => {
    return {
      title,
      amount,
      renewalPeriod,
      limit,
      costPerReport: planCostPerReport({ amount, renewalPeriod, limit }),
    };
  }, [amount, limit, renewalPeriod, title]);

  const customUpgradeOption =
    customPlanUpgrade?.data?.subscription?.custom_upgrade_option;
  const upgradePlans = brandPlans?.data?.subscription?.upgrade_options;
  const hasPlans = plans.length > 0;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (!accountLoaded) {
      return;
    }

    let selectedPlans;
    if (customUpgradeOption) {
      selectedPlans = getCustomPlan(customUpgradeOption, currentPlan);
    } else if (upgradePlans) {
      selectedPlans = getPlans(
        upgradePlans,
        currentPlan,
        isInDownsellVariation
      );
    }
    if (selectedPlans) {
      setPlans(selectedPlans);
    }

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [
    accountLoaded,
    currentPlan,
    upgradePlans,
    customUpgradeOption,
    isInDownsellVariation,
  ]);
  // END EFFECTS

  // START EVENT_HANDLERS
  const showDefaultError = () => {
    enqueueSnackbar(
      'Something went wrong, please try again or contact customer service',
      { variant: 'error' }
    );
  };

  const onSelectPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setBillingFrecuency(getBillingFrequency(plan.renewalPeriod));
    setBilledPrice(`$${plan.amount}`);
    setOpenTosModal(true);
  };

  const onCloseTos = () => setOpenTosModal(false);
  const onAcceptTos = () => {
    setProcessingPayment(true);
    updateLegalQuery.mutate(undefined, {
      onSuccess: () => upgradePlan(),
      onError: () => showDefaultError(),
      onSettled: () => onCloseTos(),
    });
  };

  const upgradePlan = async () => {
    const activePaymentQueryRefetched = await paymentMethodQuery.refetch();
    const activePayment = filterActivePaymentMethod(
      activePaymentQueryRefetched?.data
    );

    if (!selectedPlan || !activePayment) {
      return;
    }

    setUpgradeState(State.ProcessingPayment);

    const plan = { unique_key: selectedPlan.key };
    const queryOptions: any = {
      onSuccess: () => {
        refetchAccount();
        setUpgradeState(State.Success);
      },
      onError(error: Error) {
        let cause = error.cause as string[];
        const stringifiedErrors = cause?.join(', ');

        if (stringifiedErrors === 'Unknown Checkout Failure Reason') {
          cause = [
            'Your payment failed, please try again or choose a different payment method.',
          ];
        }

        setUpgradeErrors(cause);
        setUpgradeState(State.Error);
      },
    };

    interface MutationFunctions {
      paypal: typeof processPayPalQuery;
      braintree: typeof processBraintreeQuery;
      card: typeof processCardQuery;
    }

    const mutationFunctions: MutationFunctions = {
      paypal: processPayPalQuery,
      braintree: processBraintreeQuery,
      card: processCardQuery,
    };

    if (getPaymentType(activePayment) === 'unified') {
      setProcessingPayment(false);
      return;
    }

    const getSupportedPaymentType = (
      payment: PaymentMethods
    ): 'card' | 'braintree' | 'paypal' => {
      const paymentType = getPaymentType(payment);
      if (
        paymentType === 'card' ||
        paymentType === 'braintree' ||
        paymentType === 'paypal'
      ) {
        return paymentType;
      }
      return 'card'; // default fallback
    };
    const paymentType: 'card' | 'braintree' | 'paypal' =
      getSupportedPaymentType(activePayment);

    const mutationFunction = mutationFunctions[paymentType] || processCardQuery;

    const mutationParams = {
      plan,
      ...((paymentType === 'paypal' && {
        paypalId: activePayment.id,
      }) as { paypalId: number }),
      ...((paymentType === 'card' && {
        cardId: activePayment.id,
      }) as {
        cardId: number;
      }),
      ...((paymentType === 'braintree' && {
        braintreeId: activePayment.id,
      }) as { braintreeId: number }),
      source_of_payment: 'manual_upgrade_attempt',
    };
    setProcessingPayment(false);
    mutationFunction.mutate(mutationParams, queryOptions);
  };
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <UpgradePlanContent container={hasPlans}>
      {isLoading && <CircularProgress sx={{ my: 30 }}></CircularProgress>}

      {!isLoading && (
        <>
          <UpgradePlan
            currentPlan={{
              title: currentPlan.title,
              limit: isLimitedUser ? currentPlan.limit : 'unlimited',
              costPerReport: currentPlan.costPerReport,
              canceled: isCancelledSubscription,
            }}
            plans={plans}
            onSelectPlan={onSelectPlan}
            onContactUs={contactUsHandler}
          />

          <TosModal
            open={openTosModal}
            showCheckbox={true}
            billingFrecuency={billingFrecuency}
            billedPrice={billedPrice}
            cta={{
              text:
                selectedPlan?.title === 'Saver'
                  ? 'Accept and Downgrade'
                  : 'Accept and Upgrade',
              extraWide: true,
              processingPayment,
            }}
            showPaymentMethod={true}
            acceptTos={onAcceptTos}
            onChangePaymentMethod={() => setUpgradeState(State.SelectPayment)}
            onClose={onCloseTos}
          />

          <PaymentsSelectionModal
            open={isState(State.SelectPayment)}
            cta="Use This Payment Method"
            onAddPaymentMethod={() => setUpgradeState(State.AddPayment)}
            onSelectedPaymentMethod={() => {
              if (hadPaymentErrors) {
                setHadPaymentErrors(false);
                upgradePlan();
              } else {
                setUpgradeState(State.None);
              }
            }}
            onClose={() => {
              if (hadPaymentErrors) {
                setHadPaymentErrors(false);
                setUpgradeState(State.Error);
              } else {
                setUpgradeState(State.None);
              }
            }}
          />

          <RadioPaymentsModal
            isOpen={isState(State.AddPayment)}
            onCloseHandle={() => setUpgradeState(State.None)}
          />

          <ProcessingSpinnerModal open={isState(State.ProcessingPayment)} />

          <UpgradeSuccessModal
            open={isState(State.Success)}
            onAccept={() => {
              setUpgradeState(State.None);

              navigate('/dashboard');
            }}
          />

          <UpgradeFailureModal
            open={isState(State.Error)}
            errors={upgradeErrors}
            onTryAgain={() => upgradePlan()}
            onChangeCard={() => {
              setHadPaymentErrors(true);
              setUpgradeState(State.SelectPayment);
            }}
            onClose={() => {
              setUpgradeState(State.None);
            }}
          />
        </>
      )}
    </UpgradePlanContent>
  );
  // END RENDER
};
